/**
 * VideoCallScorecardPanel.tsx
 *
 * Presentational AI-scorecard panel for the Video Call modal (right column).
 * Overall score ring + per-field bars/scores + summary takeaways + expandable
 * per-field detail (feedback bullets and timecoded citations). Functional parity
 * with the reference; our own light-slate styling.
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, RotateCcw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';
import type { ScorecardFieldResult, ScorecardResult } from '@/services/videoCalls.service';

export type ScorecardEmptyReason = 'no-template' | 'no-transcript' | null;

interface Props {
  scorecard: ScorecardResult | null;
  templateName?: string;
  ratingMin: number;
  ratingMax: number;
  generating: boolean;
  error?: string | null;
  emptyReason?: ScorecardEmptyReason;
  onRegenerate: () => void;
}

/** ratio → tailwind classes for text (ring/score) and bar background. */
function toneFor(score: number | null, ratingMin: number, ratingMax: number) {
  if (score == null) return { text: 'text-slate-400', bar: 'bg-slate-300' };
  const span = ratingMax - ratingMin;
  const ratio = span > 0 ? (score - ratingMin) / span : 0;
  if (ratio < 0.5) return { text: 'text-red-500', bar: 'bg-red-500' };
  if (ratio < 0.75) return { text: 'text-amber-500', bar: 'bg-amber-500' };
  return { text: 'text-green-500', bar: 'bg-green-500' };
}

function ScoreRing({ score, ratingMin, ratingMax }: { score: number; ratingMin: number; ratingMax: number }) {
  const span = ratingMax - ratingMin;
  const pct = span > 0 ? Math.max(0, Math.min(1, (score - ratingMin) / span)) : 0;
  const r = 34;
  const c = 2 * Math.PI * r;
  const tone = toneFor(score, ratingMin, ratingMax);
  return (
    <div className="relative h-24 w-24 shrink-0">
      <svg viewBox="0 0 80 80" className="h-24 w-24 -rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" strokeWidth="8" className="stroke-slate-200" />
        <circle
          cx="40"
          cy="40"
          r={r}
          fill="none"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          className={cn('transition-[stroke-dashoffset] duration-500', tone.text)}
          stroke="currentColor"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-semibold text-slate-900">{score.toFixed(1)}</span>
        <span className="text-[10px] text-slate-400">out of {ratingMax}</span>
      </div>
    </div>
  );
}

function FieldRow({ field, ratingMin, ratingMax }: { field: ScorecardFieldResult; ratingMin: number; ratingMax: number }) {
  const [open, setOpen] = useState(false);
  const tone = toneFor(field.score, ratingMin, ratingMax);
  const span = ratingMax - ratingMin;
  const pct = field.score != null && span > 0 ? ((field.score - ratingMin) / span) * 100 : 0;
  const hasDetail = field.bullets.length > 0 || field.citations.length > 0;

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        type="button"
        onClick={() => hasDetail && setOpen((o) => !o)}
        className={cn(
          'flex w-full items-center gap-3 py-2.5 text-left',
          hasDetail && 'cursor-pointer',
        )}
      >
        {hasDetail ? (
          open ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
               : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
        ) : (
          <span className="h-4 w-4 shrink-0" />
        )}
        <span className="w-40 shrink-0 truncate text-sm font-medium text-slate-700" title={field.name}>
          {field.name}
        </span>
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
          <div className={cn('h-full rounded-full transition-[width] duration-500', tone.bar)} style={{ width: `${pct}%` }} />
        </div>
        <span className={cn('w-6 shrink-0 text-right text-sm font-semibold', tone.text)}>
          {field.score != null ? field.score : '—'}
        </span>
      </button>

      {open && hasDetail && (
        <div className="space-y-3 pb-3 pl-7 pr-1">
          {field.bullets.length > 0 && (
            <ul className="list-disc space-y-1 pl-4 text-sm text-slate-600">
              {field.bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          )}
          {field.citations.length > 0 && (
            <div className="space-y-1.5">
              {field.citations.map((c, i) => (
                <div key={i} className="flex gap-2 text-xs">
                  {c.timecode && (
                    <span className="shrink-0 font-mono font-medium text-indigo-500">{c.timecode}</span>
                  )}
                  <span className="text-slate-500">{c.quote}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function VideoCallScorecardPanel({
  scorecard,
  templateName,
  ratingMin,
  ratingMax,
  generating,
  error,
  emptyReason,
  onRegenerate,
}: Props) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <header className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-slate-900">
            {templateName || 'Scorecard'}
          </h3>
        </div>
        {scorecard && !generating && (
          <Button variant="ghost" size="sm" onClick={onRegenerate} className="h-7 px-2 text-xs">
            <RotateCcw className="mr-1 h-3.5 w-3.5" />
            Regenerate
          </Button>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {generating ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <Spinner size="lg" />
            <p className="text-sm text-slate-500">Generating scorecard…</p>
          </div>
        ) : error ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <p className="text-sm text-slate-500">{error}</p>
            <Button variant="secondary" size="sm" onClick={onRegenerate}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Try again
            </Button>
          </div>
        ) : !scorecard ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <Sparkles className="h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">
              {emptyReason === 'no-template'
                ? 'No active scorecard — configure one in Admin → Account → Scorecards.'
                : emptyReason === 'no-transcript'
                  ? 'No transcript available to score yet.'
                  : 'No scorecard available.'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Overall */}
            <div className="flex items-center gap-4">
              <ScoreRing score={scorecard.overallScore} ratingMin={ratingMin} ratingMax={ratingMax} />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-700">Overall score</p>
                <p className="text-xs text-slate-400">
                  Weighted across {scorecard.result.length} field{scorecard.result.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            {/* Per-field bars (expandable) */}
            <div className="rounded-lg border border-slate-200 px-3">
              {scorecard.result.map((f) => (
                <FieldRow key={f.fieldId} field={f} ratingMin={ratingMin} ratingMax={ratingMax} />
              ))}
            </div>

            {/* Summary takeaways */}
            {scorecard.summaryTakeaways.length > 0 && (
              <ul className="list-disc space-y-2 rounded-lg bg-slate-50 p-4 pl-8 text-sm text-slate-600">
                {scorecard.summaryTakeaways.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
